/**
 * Localization - Handles game translations
 */

export type Language = 'en' | 'fr';

interface TranslationStrings {
  // Menu
  menu_continue: string;
  menu_new_game: string;
  menu_load_game: string;
  menu_save_game: string;
  menu_save_as: string;
  menu_resume: string;
  menu_language: string;
  menu_paused: string;

  // HUD
  hud_day: string;
  hud_hp: string;
  hud_sp: string;

  // Instructions
  instr_move: string;
  instr_attack: string;
  instr_inventory: string;
  instr_crafting: string;
  instr_build: string;
  instr_hotbar: string;
  instr_use: string;
  instr_zoom: string;
  // Structure interaction hints
  hint_campfire: string;
  hint_chest: string;
  hint_workbench: string;
  hint_press_e: string;

  // Panels
  panel_inventory: string;
  panel_crafting: string;
  panel_build: string;
  panel_storage: string;
  panel_campfire: string;
  panel_save_load: string;

  // Storage
  storage_chest: string;
  storage_your_inventory: string;
  storage_click_transfer: string;

  // Crafting
  craft_requires: string;
  craft_station: string;
  craft_by_hand: string;
  craft_workbench: string;
  craft_button: string;
  craft_missing: string;
  craft_ingredients: string;
  craft_result: string;
  craft_filter_all: string;
  craft_filter_tools: string;
  craft_filter_weapons: string;
  craft_filter_gear: string;
  craft_filter_items: string;
  craft_filter_ammo: string;

  // Building
  build_cost: string;
  build_select: string;
  build_place: string;
  build_cancel: string;

  // Campfire
  campfire_fuel: string;
  campfire_add_fuel: string;
  campfire_cooking: string;
  campfire_cook: string;
  campfire_burning: string;
  campfire_extinguished: string;

  // Save/Load
  save_slot: string;
  save_empty: string;
  save_confirm: string;
  save_delete: string;
  save_load: string;
  save_new: string;
  save_day: string;
  save_playtime: string;
  save_title_save: string;
  save_title_load: string;
  save_btn_load: string;
  save_btn_save: string;
  save_btn_rename: string;
  save_btn_export: string;
  save_btn_delete: string;
  save_btn_import: string;
  save_btn_new_save: string;
  save_btn_new_game: string;
  save_scroll_up: string;
  save_scroll_down: string;
  save_demo_info: string;
  save_demo_readonly: string;
  save_hint_save: string;
  save_hint_rename: string;
  save_new_save_hint: string;

  // Game Over
  gameover_title: string;
  gameover_survived: string;
  gameover_restart: string;

  // Tooltips
  tooltip_value: string;

  // Item types
  type_resource: string;
  type_tool: string;
  type_weapon: string;
  type_armor: string;
  type_consumable: string;
  type_material: string;

  // Resources
  res_wood: string;
  res_stone: string;
  res_fiber: string;
  res_iron: string;
  res_food: string;

  // Common actions
  action_gather: string;
  action_attack: string;
  action_use: string;

  // Notifications
  notify_saved: string;
  notify_loaded: string;
  notify_auto_saved: string;
  notify_loot_dropped: string;
  notify_workbench: string;
  notify_picked_up: string;

  // Torch
  torch_lit: string;
  torch_extinguished: string;
  torch_burnt_out: string;

  // Equipment Panel
  equip_title: string;
  equip_no_equipment: string;
  equip_slot_head: string;
  equip_slot_chest: string;
  equip_slot_hands: string;
  equip_slot_legs: string;
  equip_slot_feet: string;
  equip_slot_accessory: string;
  equip_stat_def: string;
  equip_stat_hp: string;
  equip_stat_speed: string;
  equip_stat_atk: string;
  equip_tooltip_defense: string;
  equip_tooltip_max_hp: string;
  equip_hint_unequip: string;
}

const translations: Record<Language, TranslationStrings> = {
  en: {
    // Menu
    menu_continue: 'Continue',
    menu_new_game: 'New Game',
    menu_load_game: 'Load Game',
    menu_save_game: 'Save Game',
    menu_save_as: 'Save As...',
    menu_resume: 'Resume',
    menu_language: 'Language',
    menu_paused: 'PAUSED',

    // HUD
    hud_day: 'Day',
    hud_hp: 'HP',
    hud_sp: 'SP',

    // Instructions
    instr_move: 'WASD - Move',
    instr_attack: 'LMB - Attack/Gather',
    instr_inventory: 'I - Inventory',
    instr_crafting: 'C - Crafting',
    instr_build: 'B - Build',
    instr_hotbar: '1-0 - Hotbar',
    instr_use: 'E - Use/Toggle',
    instr_zoom: '+/- - Zoom',
    hint_campfire: 'Campfire - Add wood to fuel, cook food',
    hint_chest: 'Chest - Store items',
    hint_workbench: 'Workbench - Craft advanced items',
    hint_press_e: 'Press left click to interact',

    // Panels
    panel_inventory: 'INVENTORY',
    panel_crafting: 'CRAFTING',
    panel_build: 'BUILD',
    panel_storage: 'STORAGE',
    panel_campfire: 'CAMPFIRE',
    panel_save_load: 'SAVE / LOAD',

    // Storage
    storage_chest: 'CHEST:',
    storage_your_inventory: 'YOUR INVENTORY:',
    storage_click_transfer: 'Click items to transfer',

    // Crafting
    craft_requires: 'Requires:',
    craft_station: 'Station:',
    craft_by_hand: 'By Hand',
    craft_workbench: 'Workbench',
    craft_button: 'CRAFT',
    craft_missing: 'Missing materials',
    craft_ingredients: 'Ingredients:',
    craft_result: 'Result:',
    craft_filter_all: 'All',
    craft_filter_tools: 'Tools',
    craft_filter_weapons: 'Weapons',
    craft_filter_gear: 'Gear',
    craft_filter_items: 'Items',
    craft_filter_ammo: 'Ammo',

    // Building
    build_cost: 'Cost:',
    build_select: 'Select a structure to build',
    build_place: 'Click to place',
    build_cancel: 'ESC to cancel',

    // Campfire
    campfire_fuel: 'Fuel',
    campfire_add_fuel: 'Add Fuel',
    campfire_cooking: 'Cooking',
    campfire_cook: 'Cook',
    campfire_burning: 'Burning',
    campfire_extinguished: 'Extinguished',

    // Save/Load
    save_slot: 'Slot',
    save_empty: 'Empty Slot',
    save_confirm: 'Save',
    save_delete: 'Delete',
    save_load: 'Load',
    save_new: 'New Game',
    save_day: 'Day',
    save_playtime: 'Playtime',
    save_title_save: 'SAVE GAME',
    save_title_load: 'LOAD GAME',
    save_btn_load: 'LOAD',
    save_btn_save: 'SAVE',
    save_btn_rename: 'RENAME',
    save_btn_export: 'EXPORT',
    save_btn_delete: 'DELETE',
    save_btn_import: 'IMPORT',
    save_btn_new_save: 'NEW SAVE',
    save_btn_new_game: 'NEW GAME',
    save_scroll_up: 'Scroll up',
    save_scroll_down: 'Scroll down',
    save_demo_info: '(Built-in demo - cannot be modified)',
    save_demo_readonly: 'Built-in demo save (read-only)',
    save_hint_save: 'Enter=Save, Esc=Cancel',
    save_hint_rename: 'Enter=Rename, Esc=Cancel',
    save_new_save_hint: 'New save - type a name',

    // Game Over
    gameover_title: 'GAME OVER',
    gameover_survived: 'Survived {0} days',
    gameover_restart: 'Press R or Click to Restart',

    // Tooltips
    tooltip_value: 'Value',

    // Item types
    type_resource: 'Resource',
    type_tool: 'Tool',
    type_weapon: 'Weapon',
    type_armor: 'Armor',
    type_consumable: 'Consumable',
    type_material: 'Material',

    // Resources
    res_wood: 'Wood',
    res_stone: 'Stone',
    res_fiber: 'Plant Fiber',
    res_iron: 'Iron',
    res_food: 'Food',

    // Common actions
    action_gather: 'Gather',
    action_attack: 'Attack',
    action_use: 'Use',

    // Notifications
    notify_saved: 'Game saved!',
    notify_loaded: 'Game loaded!',
    notify_auto_saved: 'Auto-saved',
    notify_loot_dropped: 'Loot dropped!',
    notify_workbench: 'Workbench: Advanced crafting',
    notify_picked_up: '+{0} {1}',

    // Torch
    torch_lit: 'Torch lit',
    torch_extinguished: 'Torch extinguished',
    torch_burnt_out: 'Torch burnt out!',

    // Equipment Panel
    equip_title: 'EQUIPMENT',
    equip_no_equipment: 'No equipment',
    equip_slot_head: 'Head',
    equip_slot_chest: 'Chest',
    equip_slot_hands: 'Hands',
    equip_slot_legs: 'Legs',
    equip_slot_feet: 'Feet',
    equip_slot_accessory: 'Accessory',
    equip_stat_def: 'DEF:',
    equip_stat_hp: 'HP',
    equip_stat_speed: 'Speed',
    equip_stat_atk: 'ATK',
    equip_tooltip_defense: 'Defense:',
    equip_tooltip_max_hp: 'Max HP',
    equip_hint_unequip: 'Double-click to unequip'
  },

  fr: {
    // Menu
    menu_continue: 'Continuer',
    menu_new_game: 'Nouvelle partie',
    menu_load_game: 'Charger',
    menu_save_game: 'Sauvegarder',
    menu_save_as: 'Sauvegarder sous...',
    menu_resume: 'Reprendre',
    menu_language: 'Langue',
    menu_paused: 'PAUSE',

    // HUD
    hud_day: 'Jour',
    hud_hp: 'PV',
    hud_sp: 'EN',

    // Instructions
    instr_move: 'WASD - Déplacer',
    instr_attack: 'Clic G - Attaquer/Récolter',
    instr_inventory: 'I - Inventaire',
    instr_crafting: 'C - Fabrication',
    instr_build: 'B - Construire',
    instr_hotbar: '1-0 - Raccourcis',
    instr_use: 'E - Utiliser',
    instr_zoom: '+/- - Zoom',
    hint_campfire: 'Feu - Ajouter du bois, cuire',
    hint_chest: 'Coffre - Stocker objets',
    hint_workbench: 'Établi - Fabriquer objets avancés',
    hint_press_e: 'Cliquez gauche pour interagir',

    // Panels
    panel_inventory: 'INVENTAIRE',
    panel_crafting: 'FABRICATION',
    panel_build: 'CONSTRUCTION',
    panel_storage: 'STOCKAGE',
    panel_campfire: 'FEU DE CAMP',
    panel_save_load: 'SAUVEGARDER / CHARGER',

    // Storage
    storage_chest: 'COFFRE:',
    storage_your_inventory: 'VOTRE INVENTAIRE:',
    storage_click_transfer: 'Cliquez pour transférer',

    // Crafting
    craft_requires: 'Nécessite:',
    craft_station: 'Station:',
    craft_by_hand: 'À la main',
    craft_workbench: 'Établi',
    craft_button: 'FABRIQUER',
    craft_missing: 'Matériaux manquants',
    craft_ingredients: 'Ingrédients:',
    craft_result: 'Résultat:',
    craft_filter_all: 'Tout',
    craft_filter_tools: 'Outils',
    craft_filter_weapons: 'Armes',
    craft_filter_gear: 'Équip.',
    craft_filter_items: 'Objets',
    craft_filter_ammo: 'Munit.',

    // Building
    build_cost: 'Coût:',
    build_select: 'Choisir une structure',
    build_place: 'Cliquez pour placer',
    build_cancel: 'ESC pour annuler',

    // Campfire
    campfire_fuel: 'Combustible',
    campfire_add_fuel: 'Ajouter',
    campfire_cooking: 'Cuisson',
    campfire_cook: 'Cuire',
    campfire_burning: 'Allumé',
    campfire_extinguished: 'Éteint',

    // Save/Load
    save_slot: 'Emplacement',
    save_empty: 'Emplacement vide',
    save_confirm: 'Sauvegarder',
    save_delete: 'Supprimer',
    save_load: 'Charger',
    save_new: 'Nouvelle partie',
    save_day: 'Jour',
    save_playtime: 'Temps de jeu',
    save_title_save: 'SAUVEGARDER',
    save_title_load: 'CHARGER',
    save_btn_load: 'CHARGER',
    save_btn_save: 'SAUVER',
    save_btn_rename: 'RENOMMER',
    save_btn_export: 'EXPORTER',
    save_btn_delete: 'SUPPR.',
    save_btn_import: 'IMPORTER',
    save_btn_new_save: 'NOUVELLE',
    save_btn_new_game: 'NOUVELLE PARTIE',
    save_scroll_up: 'Défiler vers le haut',
    save_scroll_down: 'Défiler vers le bas',
    save_demo_info: '(Démo intégrée - non modifiable)',
    save_demo_readonly: 'Sauvegarde démo (lecture seule)',
    save_hint_save: 'Entrée=Sauver, Échap=Annuler',
    save_hint_rename: 'Entrée=Renommer, Échap=Annuler',
    save_new_save_hint: 'Nouvelle sauvegarde - entrez un nom',

    // Game Over
    gameover_title: 'FIN DE PARTIE',
    gameover_survived: 'Survécu {0} jours',
    gameover_restart: 'Appuyez sur R ou Cliquez',

    // Tooltips
    tooltip_value: 'Valeur',

    // Item types
    type_resource: 'Ressource',
    type_tool: 'Outil',
    type_weapon: 'Arme',
    type_armor: 'Armure',
    type_consumable: 'Consommable',
    type_material: 'Matériau',

    // Resources
    res_wood: 'Bois',
    res_stone: 'Pierre',
    res_fiber: 'Fibre végétale',
    res_iron: 'Fer',
    res_food: 'Nourriture',

    // Common actions
    action_gather: 'Récolter',
    action_attack: 'Attaquer',
    action_use: 'Utiliser',

    // Notifications
    notify_saved: 'Partie sauvegardée!',
    notify_loaded: 'Partie chargée!',
    notify_auto_saved: 'Sauvegarde auto',
    notify_loot_dropped: 'Butin obtenu!',
    notify_workbench: 'Établi: Fabrication avancée',
    notify_picked_up: '+{0} {1}',

    // Torch
    torch_lit: 'Torche allumée',
    torch_extinguished: 'Torche éteinte',
    torch_burnt_out: 'Torche consumée!',

    // Equipment Panel
    equip_title: 'ÉQUIPEMENT',
    equip_no_equipment: 'Aucun équipement',
    equip_slot_head: 'Tête',
    equip_slot_chest: 'Torse',
    equip_slot_hands: 'Mains',
    equip_slot_legs: 'Jambes',
    equip_slot_feet: 'Pieds',
    equip_slot_accessory: 'Accessoire',
    equip_stat_def: 'DÉF:',
    equip_stat_hp: 'PV',
    equip_stat_speed: 'Vitesse',
    equip_stat_atk: 'ATQ',
    equip_tooltip_defense: 'Défense:',
    equip_tooltip_max_hp: 'PV Max',
    equip_hint_unequip: 'Double-clic pour retirer'
  }
};

// Item name translations
const itemTranslations: Record<Language, Record<string, { name: string; description: string }>> = {
  en: {
    // Resources
    wood: { name: 'Wood', description: 'Basic building material.' },
    stone: { name: 'Stone', description: 'Hard mineral for tools and building.' },
    fiber: { name: 'Plant Fiber', description: 'Fibrous plant material.' },
    iron_ore: { name: 'Iron Ore', description: 'Raw iron ore.' },
    coal: { name: 'Coal', description: 'Fuel for furnaces.' },

    // Food
    apple: { name: 'Apple', description: 'A fresh apple.' },
    cooked_meat: { name: 'Cooked Meat', description: 'Delicious cooked meat.' },
    raw_meat: { name: 'Raw Meat', description: 'Raw meat. Cook it first!' },

    // Tools
    axe_wood: { name: 'Wooden Axe', description: 'A basic axe for chopping trees.' },
    axe_stone: { name: 'Stone Axe', description: 'An improved axe.' },
    axe_iron: { name: 'Iron Axe', description: 'A sturdy iron axe.' },
    pickaxe_wood: { name: 'Wooden Pickaxe', description: 'A basic pickaxe for mining.' },
    pickaxe_stone: { name: 'Stone Pickaxe', description: 'An improved pickaxe.' },
    pickaxe_iron: { name: 'Iron Pickaxe', description: 'A sturdy iron pickaxe.' },
    sickle_wood: { name: 'Wooden Sickle', description: 'A basic sickle for harvesting plants.' },
    sickle_stone: { name: 'Stone Sickle', description: 'An improved sickle.' },
    torch: { name: 'Torch', description: 'A handheld light source. Press E to toggle.' },

    // Weapons
    sword_wood: { name: 'Wooden Sword', description: 'A basic wooden sword.' },
    sword_stone: { name: 'Stone Sword', description: 'A stone-bladed sword.' },
    sword_iron: { name: 'Iron Sword', description: 'A proper iron sword.' },
    spear_wood: { name: 'Wooden Spear', description: 'A simple spear with long reach.' },
    spear_stone: { name: 'Stone Spear', description: 'A stone-tipped spear.' },
    bone_knife: { name: 'Bone Knife', description: 'A fast dagger carved from bone.' },
    bone_spear: { name: 'Bone Spear', description: 'A spear tipped with bone.' },
    golem_sword: { name: 'Golem Sword', description: 'A powerful sword infused with golem energy.' },
    wolf_fang_dagger: { name: 'Wolf Fang Dagger', description: 'A swift dagger made from wolf fangs.' },
    bone_club: { name: 'Bone Club', description: 'A heavy club made of bone.' },
    orc_cleaver: { name: 'Orc Cleaver', description: 'A brutal cleaver wielded by orcs.' },
    golem_hammer: { name: 'Golem Hammer', description: 'A massive hammer infused with earth magic.' },
    demon_blade: { name: 'Demon Blade', description: 'A cursed blade burning with hellfire.' },
    spectral_scythe: { name: 'Spectral Scythe', description: 'A ghostly scythe that phases through armor.' },
    spider_fang: { name: 'Spider Fang', description: 'A venomous spider fang fashioned into a dagger.' },

    // Monster drops
    bone: { name: 'Bone', description: 'A sturdy bone from a skeleton.' },
    ectoplasm: { name: 'Ectoplasm', description: 'Ghostly residue.' },
    spider_eye: { name: 'Spider Eye', description: 'A creepy spider eye.' },
    wolf_pelt: { name: 'Wolf Pelt', description: 'Warm fur from a wolf.' },
    golem_core: { name: 'Golem Core', description: 'The magical core of a golem.' },
    demon_heart: { name: 'Demon Heart', description: 'Still beating with dark energy.' },

    // Consumables
    bandage: { name: 'Bandage', description: 'Cloth bandages for wounds.' },
    health_potion: { name: 'Health Potion', description: 'Restores health.' },
    antidote: { name: 'Antidote', description: 'Cures poison.' },
    fur_cloak: { name: 'Fur Cloak', description: 'A warm cloak from wolf pelts.' },
    spirit_essence: { name: 'Spirit Essence', description: 'Concentrated ghost energy.' },

    // Regular Tier - Cloth Armor
    cloth_hood: { name: 'Cloth Hood', description: 'A simple hood woven from plant fibers.' },
    cloth_shirt: { name: 'Cloth Shirt', description: 'A basic shirt offering minimal protection.' },
    cloth_pants: { name: 'Cloth Pants', description: 'Simple cloth leg coverings.' },
    cloth_sandals: { name: 'Cloth Sandals', description: 'Light sandals for nimble movement.' },
    cloth_wraps: { name: 'Cloth Wraps', description: 'Cloth wrappings for the hands.' },

    // Regular Tier - Leather Armor
    leather_cap: { name: 'Leather Cap', description: 'A simple leather cap.' },
    leather_tunic: { name: 'Leather Tunic', description: 'A sturdy leather tunic.' },
    leather_pants: { name: 'Leather Pants', description: 'Tough leather leg protection.' },
    leather_boots: { name: 'Leather Boots', description: 'Comfortable leather boots.' },
    leather_gloves: { name: 'Leather Gloves', description: 'Protective leather gloves.' },

    // Magic Tier - Iron Armor
    iron_helm: { name: 'Iron Helm', description: 'A sturdy iron helmet.' },
    iron_chestplate: { name: 'Iron Chestplate', description: 'Heavy iron chest protection.' },
    iron_greaves: { name: 'Iron Greaves', description: 'Protective iron leg armor.' },
    iron_boots: { name: 'Iron Boots', description: 'Heavy iron boots.' },

    // Magic Tier - Bone Armor
    bone_helm: { name: 'Bone Helm', description: 'A menacing helmet crafted from bones.' },
    bone_armor: { name: 'Bone Armor', description: 'Ribcage armor held together by sinew.' },
    bone_greaves: { name: 'Bone Greaves', description: 'Leg guards made from large bones.' },
    bone_boots: { name: 'Bone Boots', description: 'Boots reinforced with bone plates.' },

    // Magic Tier - Wolf Armor
    wolf_hood: { name: 'Wolf Hood', description: 'A hood made from wolf hide with ears intact.' },
    wolf_pelt_cloak: { name: 'Wolf Pelt Cloak', description: 'A warm cloak made from wolf pelts.' },
    wolf_pants: { name: 'Wolf Leggings', description: 'Warm leggings from wolf pelts.' },
    wolf_boots: { name: 'Wolf Boots', description: 'Soft boots for silent movement.' },
    wolf_gloves: { name: 'Wolf Gloves', description: 'Clawed gloves made from wolf hide.' },

    // Epic Tier - Golem Armor
    golem_helm: { name: 'Golem Helm', description: 'A helmet of living stone, infused with earth magic.' },
    golem_chestplate: { name: 'Golem Chestplate', description: 'Massive stone armor pulsing with ancient power.' },
    golem_greaves: { name: 'Golem Greaves', description: 'Leg armor of enchanted stone.' },
    golem_gauntlets: { name: 'Golem Gauntlets', description: 'Gauntlets forged from golem cores.' },
    golem_boots: { name: 'Golem Boots', description: 'Heavy boots that shake the ground.' },

    // Epic Tier - Demon Armor
    demon_crown: { name: 'Demon Crown', description: 'A sinister crown pulsing with dark energy.' },
    demon_chestplate: { name: 'Demon Chestplate', description: 'Armor forged in hellfire, radiating malice.' },
    demon_greaves: { name: 'Demon Greaves', description: 'Leg armor burning with infernal energy.' },
    demon_boots: { name: 'Demon Boots', description: 'Boots that leave smoldering footprints.' },
    demon_gloves: { name: 'Demon Gauntlets', description: 'Gauntlets wreathed in dark flames.' },

    // Epic Tier - Spectral Armor
    spectral_hood: { name: 'Spectral Hood', description: 'A translucent hood shimmering with ghostly energy.' },
    spectral_robe: { name: 'Spectral Robe', description: 'A flowing robe that phases through attacks.' },
    spectral_leggings: { name: 'Spectral Leggings', description: 'Ghostly leg wrappings that enhance agility.' },
    spectral_boots: { name: 'Spectral Boots', description: 'Boots that barely touch the ground.' },
    spectral_gloves: { name: 'Spectral Gloves', description: 'Gloves that pass through solid matter.' },
    spectral_amulet: { name: 'Spectral Amulet', description: 'An amulet infused with ghostly essence.' },

    // Accessories
    warriors_ring: { name: "Warrior's Ring", description: 'A ring that empowers the wearer in combat.' }
  },

  fr: {
    // Resources
    wood: { name: 'Bois', description: 'Matériau de construction de base.' },
    stone: { name: 'Pierre', description: 'Minéral dur pour outils et construction.' },
    fiber: { name: 'Fibre végétale', description: 'Matière végétale fibreuse.' },
    iron_ore: { name: 'Minerai de fer', description: 'Minerai de fer brut.' },
    coal: { name: 'Charbon', description: 'Combustible pour fournaise.' },

    // Food
    apple: { name: 'Pomme', description: 'Une pomme fraîche.' },
    cooked_meat: { name: 'Viande cuite', description: 'Délicieuse viande cuite.' },
    raw_meat: { name: 'Viande crue', description: 'Viande crue. À cuire!' },

    // Tools
    axe_wood: { name: 'Hache en bois', description: 'Une hache de base pour couper.' },
    axe_stone: { name: 'Hache en pierre', description: 'Une hache améliorée.' },
    axe_iron: { name: 'Hache en fer', description: 'Une solide hache en fer.' },
    pickaxe_wood: { name: 'Pioche en bois', description: 'Une pioche de base.' },
    pickaxe_stone: { name: 'Pioche en pierre', description: 'Une pioche améliorée.' },
    pickaxe_iron: { name: 'Pioche en fer', description: 'Une solide pioche en fer.' },
    sickle_wood: { name: 'Faucille en bois', description: 'Une faucille de base.' },
    sickle_stone: { name: 'Faucille en pierre', description: 'Une faucille améliorée.' },
    torch: { name: 'Torche', description: 'Une source de lumière portable. Appuyez sur E pour allumer.' },

    // Weapons
    sword_wood: { name: 'Épée en bois', description: 'Une épée en bois basique.' },
    sword_stone: { name: 'Épée en pierre', description: 'Une épée à lame de pierre.' },
    sword_iron: { name: 'Épée en fer', description: 'Une vraie épée en fer.' },
    spear_wood: { name: 'Lance en bois', description: 'Une lance simple à longue portée.' },
    spear_stone: { name: 'Lance en pierre', description: 'Une lance à pointe de pierre.' },
    bone_knife: { name: 'Couteau en os', description: 'Une dague rapide en os.' },
    bone_spear: { name: 'Lance en os', description: 'Une lance à pointe d\'os.' },
    golem_sword: { name: 'Épée de golem', description: 'Une épée puissante infusée d\'énergie.' },
    wolf_fang_dagger: { name: 'Dague croc de loup', description: 'Une dague rapide faite de crocs de loup.' },
    bone_club: { name: 'Massue en os', description: 'Une lourde massue en os.' },
    orc_cleaver: { name: 'Couperet d\'orc', description: 'Un couperet brutal manié par les orcs.' },
    golem_hammer: { name: 'Marteau de golem', description: 'Un marteau massif infusé de magie terrestre.' },
    demon_blade: { name: 'Lame démoniaque', description: 'Une lame maudite brûlant du feu infernal.' },
    spectral_scythe: { name: 'Faux spectrale', description: 'Une faux fantomatique qui traverse les armures.' },
    spider_fang: { name: 'Croc d\'araignée', description: 'Un croc d\'araignée venimeux façonné en dague.' },

    // Monster drops
    bone: { name: 'Os', description: 'Un os solide de squelette.' },
    ectoplasm: { name: 'Ectoplasme', description: 'Résidu fantomatique.' },
    spider_eye: { name: 'Oeil d\'araignée', description: 'Un oeil d\'araignée effrayant.' },
    wolf_pelt: { name: 'Peau de loup', description: 'Fourrure chaude de loup.' },
    golem_core: { name: 'Coeur de golem', description: 'Le coeur magique d\'un golem.' },
    demon_heart: { name: 'Coeur de démon', description: 'Bat encore avec une énergie sombre.' },

    // Consumables
    bandage: { name: 'Bandage', description: 'Pansements pour blessures.' },
    health_potion: { name: 'Potion de vie', description: 'Restaure la santé.' },
    antidote: { name: 'Antidote', description: 'Guérit le poison.' },
    fur_cloak: { name: 'Cape de fourrure', description: 'Une cape chaude en peau de loup.' },
    spirit_essence: { name: 'Essence spirituelle', description: 'Énergie fantomatique concentrée.' },

    // Regular Tier - Cloth Armor
    cloth_hood: { name: 'Capuche en tissu', description: 'Une capuche simple tissée de fibres végétales.' },
    cloth_shirt: { name: 'Chemise en tissu', description: 'Une chemise basique offrant peu de protection.' },
    cloth_pants: { name: 'Pantalon en tissu', description: 'Simples jambières en tissu.' },
    cloth_sandals: { name: 'Sandales en tissu', description: 'Sandales légères pour mouvement agile.' },
    cloth_wraps: { name: 'Bandages en tissu', description: 'Bandages de tissu pour les mains.' },

    // Regular Tier - Leather Armor
    leather_cap: { name: 'Casquette en cuir', description: 'Une simple casquette en cuir.' },
    leather_tunic: { name: 'Tunique en cuir', description: 'Une tunique en cuir robuste.' },
    leather_pants: { name: 'Pantalon en cuir', description: 'Protection solide pour les jambes.' },
    leather_boots: { name: 'Bottes en cuir', description: 'Bottes en cuir confortables.' },
    leather_gloves: { name: 'Gants en cuir', description: 'Gants en cuir protecteurs.' },

    // Magic Tier - Iron Armor
    iron_helm: { name: 'Casque en fer', description: 'Un casque en fer robuste.' },
    iron_chestplate: { name: 'Plastron en fer', description: 'Protection lourde pour le torse.' },
    iron_greaves: { name: 'Jambières en fer', description: 'Armure de jambes en fer.' },
    iron_boots: { name: 'Bottes en fer', description: 'Bottes en fer lourdes.' },

    // Magic Tier - Bone Armor
    bone_helm: { name: 'Casque d\'os', description: 'Un casque menaçant fait d\'os.' },
    bone_armor: { name: 'Armure d\'os', description: 'Armure de côtes maintenue par des tendons.' },
    bone_greaves: { name: 'Jambières d\'os', description: 'Protège-jambes en gros os.' },
    bone_boots: { name: 'Bottes d\'os', description: 'Bottes renforcées de plaques d\'os.' },

    // Magic Tier - Wolf Armor
    wolf_hood: { name: 'Capuche de loup', description: 'Une capuche en peau de loup avec oreilles.' },
    wolf_pelt_cloak: { name: 'Cape de loup', description: 'Une cape chaude en peaux de loup.' },
    wolf_pants: { name: 'Jambières de loup', description: 'Jambières chaudes en peau de loup.' },
    wolf_boots: { name: 'Bottes de loup', description: 'Bottes souples pour mouvement silencieux.' },
    wolf_gloves: { name: 'Gants de loup', description: 'Gants griffus en peau de loup.' },

    // Epic Tier - Golem Armor
    golem_helm: { name: 'Casque de golem', description: 'Un casque de pierre vivante, infusé de magie terrestre.' },
    golem_chestplate: { name: 'Plastron de golem', description: 'Armure de pierre massive pulsant de pouvoir ancien.' },
    golem_greaves: { name: 'Jambières de golem', description: 'Armure de jambes en pierre enchantée.' },
    golem_gauntlets: { name: 'Gantelets de golem', description: 'Gantelets forgés à partir de cœurs de golem.' },
    golem_boots: { name: 'Bottes de golem', description: 'Bottes lourdes qui font trembler le sol.' },

    // Epic Tier - Demon Armor
    demon_crown: { name: 'Couronne démoniaque', description: 'Une couronne sinistre pulsant d\'énergie sombre.' },
    demon_chestplate: { name: 'Plastron démoniaque', description: 'Armure forgée dans le feu infernal.' },
    demon_greaves: { name: 'Jambières démoniaques', description: 'Armure de jambes brûlant d\'énergie infernale.' },
    demon_boots: { name: 'Bottes démoniaques', description: 'Bottes qui laissent des empreintes fumantes.' },
    demon_gloves: { name: 'Gantelets démoniaques', description: 'Gantelets enveloppés de flammes sombres.' },

    // Epic Tier - Spectral Armor
    spectral_hood: { name: 'Capuche spectrale', description: 'Une capuche translucide scintillant d\'énergie fantomatique.' },
    spectral_robe: { name: 'Robe spectrale', description: 'Une robe fluide qui traverse les attaques.' },
    spectral_leggings: { name: 'Jambières spectrales', description: 'Bandages fantomatiques améliorant l\'agilité.' },
    spectral_boots: { name: 'Bottes spectrales', description: 'Bottes qui touchent à peine le sol.' },
    spectral_gloves: { name: 'Gants spectraux', description: 'Gants qui traversent la matière.' },
    spectral_amulet: { name: 'Amulette spectrale', description: 'Une amulette infusée d\'essence fantomatique.' },

    // Accessories
    warriors_ring: { name: 'Anneau du guerrier', description: 'Un anneau qui renforce le porteur au combat.' }
  }
};

// Building/Structure translations
const structureTranslations: Record<Language, Record<string, { name: string; description: string }>> = {
  en: {
    wall_wood: { name: 'Wooden Wall', description: 'A basic wooden wall.' },
    wall_stone: { name: 'Stone Wall', description: 'A sturdy stone wall.' },
    floor_wood: { name: 'Wood Floor', description: 'Walkable by player only.' },
    door_wood: { name: 'Wooden Door', description: 'Player can pass, enemies cannot.' },
    chest_wood: { name: 'Wooden Chest', description: 'Stores 20 items. Must be placed on floor.' },
    workbench: { name: 'Workbench', description: 'Craft advanced items. Click to use.' },
    campfire: { name: 'Campfire', description: 'Provides light and cooks food. Needs wood fuel.' },
    bed: { name: 'Bed', description: 'Rest and set spawn point.' },
    torch: { name: 'Torch', description: 'Provides light.' }
  },
  fr: {
    wall_wood: { name: 'Mur en bois', description: 'Un mur en bois basique.' },
    wall_stone: { name: 'Mur en pierre', description: 'Un mur en pierre solide.' },
    floor_wood: { name: 'Plancher', description: 'Accessible au joueur seulement.' },
    door_wood: { name: 'Porte en bois', description: 'Le joueur peut passer, pas les ennemis.' },
    chest_wood: { name: 'Coffre en bois', description: 'Stocke 20 objets. Doit être sur un plancher.' },
    workbench: { name: 'Établi', description: 'Fabriquer des objets avancés. Cliquer pour utiliser.' },
    campfire: { name: 'Feu de camp', description: 'Éclaire et cuit la nourriture. Besoin de bois.' },
    bed: { name: 'Lit', description: 'Se reposer et définir le point de réapparition.' },
    torch: { name: 'Torche', description: 'Fournit de la lumière.' }
  }
};

class LocalizationManager {
  private currentLanguage: Language = 'en';
  private static instance: LocalizationManager;

  private constructor() {
    // Load saved language preference
    const saved = localStorage.getItem('game_language');
    if (saved === 'en' || saved === 'fr') {
      this.currentLanguage = saved;
    }
  }

  static getInstance(): LocalizationManager {
    if (!LocalizationManager.instance) {
      LocalizationManager.instance = new LocalizationManager();
    }
    return LocalizationManager.instance;
  }

  getLanguage(): Language {
    return this.currentLanguage;
  }

  setLanguage(lang: Language): void {
    this.currentLanguage = lang;
    localStorage.setItem('game_language', lang);
  }

  toggleLanguage(): Language {
    this.currentLanguage = this.currentLanguage === 'en' ? 'fr' : 'en';
    localStorage.setItem('game_language', this.currentLanguage);
    return this.currentLanguage;
  }

  /** Get a translated string */
  t(key: keyof TranslationStrings, ...args: (string | number)[]): string {
    let text = translations[this.currentLanguage][key] || translations['en'][key] || key;

    // Replace placeholders like {0}, {1}, etc.
    args.forEach((arg, index) => {
      text = text.replace(`{${index}}`, String(arg));
    });

    return text;
  }

  /** Get translated item name and description */
  getItem(itemId: string): { name: string; description: string } | null {
    return itemTranslations[this.currentLanguage][itemId] ||
           itemTranslations['en'][itemId] ||
           null;
  }

  /** Get translated structure name and description */
  getStructure(structureId: string): { name: string; description: string } | null {
    return structureTranslations[this.currentLanguage][structureId] ||
           structureTranslations['en'][structureId] ||
           null;
  }

  /** Get language display name */
  getLanguageName(lang?: Language): string {
    const l = lang || this.currentLanguage;
    return l === 'en' ? 'English' : 'Français';
  }
}

// Export singleton instance
export const Localization = LocalizationManager.getInstance();

// Shorthand for translation
export const t = (key: keyof TranslationStrings, ...args: (string | number)[]): string => {
  return Localization.t(key, ...args);
};
